/**
 * Instagram and Facebook open links in their own in-app browser, where
 * Google sign-in is refused (SPEC_V12). We spot it from the user agent and
 * ask people to open the page in their real browser first.
 */
export type InApp = 'instagram' | 'facebook' | 'snapchat' | 'linkedin' | 'tiktok' | 'x' | 'line' | null;

/** In-app browsers (embedded web views) where Google refuses to sign anyone in. */
export function inAppBrowser(ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent): InApp {
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/\bFBAN\/|\bFBAV\/|\bFB_IAB\/|\bFBIOS\b|Messenger/.test(ua)) return 'facebook';
  if (/Snapchat/i.test(ua)) return 'snapchat';
  if (/LinkedInApp/i.test(ua)) return 'linkedin';
  if (/musical_ly|BytedanceWebview|TikTok/i.test(ua)) return 'tiktok';
  if (/\bTwitter(?:Android)?\b/i.test(ua)) return 'x';
  if (/\bLine\//.test(ua)) return 'line';
  return null;
}

/** The app's name as people know it. */
export function inAppName(app: Exclude<InApp, null>): string {
  return { instagram: 'Instagram', facebook: 'Facebook', snapchat: 'Snapchat', linkedin: 'LinkedIn', tiktok: 'TikTok', x: 'X', line: 'LINE' }[app];
}

export function isAndroid(ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent): boolean {
  return /Android/i.test(ua);
}

/** Android: an intent URL that hands the page to Chrome (or the default browser). */
export function androidIntentUrl(href: string): string {
  const u = new URL(href);
  return `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=${u.protocol.replace(':', '')};S.browser_fallback_url=${encodeURIComponent(href)};end`;
}

export function isIOS(ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent): boolean {
  return /iPhone|iPad|iPod/i.test(ua);
}

/** iOS 17+: hands an https page from an in-app browser to Safari. */
export function safariUrl(href: string): string {
  return href.replace(/^https?:\/\//, (m) => `x-safari-${m}`);
}
